import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Grid,
  InputAdornment,
  TablePagination,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as PRIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import purchaseRequisitionService, { PurchaseRequisition, PRStats } from '../services/purchaseRequisition.service';

const PRManagement: React.FC = () => {
  const [prs, setPrs] = useState<PurchaseRequisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedPR, setSelectedPR] = useState<PurchaseRequisition | null>(null);
  const [viewPR, setViewPR] = useState<PurchaseRequisition | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [attentionFilter, setAttentionFilter] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [prTypeFilter, setPrTypeFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [items, setItems] = useState<Array<{
    description: string;
    model_size: string;
    qty: number;
    unit: string;
    remark: string;
  }>>([{
    description: '',
    model_size: '',
    qty: 0,
    unit: 'PC',
    remark: ''
  }]);
  
  const [formData, setFormData] = useState<Partial<PurchaseRequisition>>({
    pr_date: format(new Date(), 'yyyy-MM-dd'),
    pr_no: '',
    item_no: '',
    pr_type: '',
    attention: '',
    description: '',
    machine: '',
    model_size: '',
    remark: '',
    status: 'waiting_for_pr',
    delivery_method: '',
    supplier: '',
    unit: 'PCS',
    qty: 0,
    sap_code: '',
    sap_pr: '',
    sap_po: '',
    grn: '',
    location: '',
    request_qty: 0,
    issued_qty: 0,
  });

  useEffect(() => {
    loadPRs();
  }, [searchTerm, statusFilter, attentionFilter, machineFilter, prTypeFilter, supplierFilter, startDate, endDate]);

  const loadPRs = async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        attention: attentionFilter || undefined,
        machine: machineFilter || undefined,
        pr_type: prTypeFilter || undefined,
        supplier: supplierFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      const response = await purchaseRequisitionService.getAll(filters);
      setPrs(response.purchaseRequisitions);
    } catch (error) {
      console.error('Failed to load PRs:', error);
      showSnackbar('Failed to load purchase requisitions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pr?: PurchaseRequisition) => {
    if (pr) {
      setSelectedPR(pr);
      setFormData({
        ...pr,
        pr_date: pr.pr_date ? format(new Date(pr.pr_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        quotation_ok_date: pr.quotation_ok_date ? format(new Date(pr.quotation_ok_date), 'yyyy-MM-dd') : '',
        received_date: pr.received_date ? format(new Date(pr.received_date), 'yyyy-MM-dd') : '',
      });
      // Load items if available
      if (pr.items && pr.items.length > 0) {
        setItems(pr.items.map(item => ({
          description: item.description || '',
          model_size: item.model_size || '',
          qty: item.qty || 0,
          unit: item.unit || 'PC',
          remark: item.remark || ''
        })));
      } else {
        // For backward compatibility with old PRs
        setItems([{
          description: pr.description || '',
          model_size: pr.model_size || '',
          qty: pr.qty || 0,
          unit: pr.unit || 'PC',
          remark: pr.remark || ''
        }]);
      }
    } else {
      setSelectedPR(null);
      setFormData({
        pr_date: format(new Date(), 'yyyy-MM-dd'),
        pr_no: '',
        item_no: '',
        pr_type: '',
        attention: '',
        description: '',
        machine: '',
        model_size: '',
        remark: '',
        status: 'waiting_for_pr',
        delivery_method: '',
        supplier: '',
        unit: 'PCS',
        qty: 0,
        sap_code: '',
        sap_pr: '',
        sap_po: '',
        grn: '',
        location: '',
        request_qty: 0,
        issued_qty: 0,
      });
      setItems([{
        description: '',
        model_size: '',
        qty: 0,
        unit: 'PC',
        remark: ''
      }]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPR(null);
  };

  const handleViewDetails = async (pr: PurchaseRequisition) => {
    // In split-row mode, details should reflect only the selected PR item row.
    setViewPR({ ...pr, items: undefined });
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setViewPR(null);
  };

  const handleInlineStatusChange = async (pr: PurchaseRequisition, nextStatus: string) => {
    if (nextStatus === pr.status) return;

    const previousStatus = pr.status;
    setUpdatingStatusId(pr.id);

    // Optimistic UI update for all rows under same PR id.
    setPrs((prev) => prev.map((row) => (row.id === pr.id ? { ...row, status: nextStatus } : row)));

    try {
      await purchaseRequisitionService.updateStatus(pr.id, nextStatus);
      showSnackbar('Status updated successfully', 'success');
    } catch (error: any) {
      setPrs((prev) => prev.map((row) => (row.id === pr.id ? { ...row, status: previousStatus } : row)));
      showSnackbar(error.response?.data?.message || 'Failed to update status', 'error');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const dataToSubmit = {
        ...formData,
        items: items.map(item => ({
          ...item,
          request_qty: item.qty
        }))
      };
      
      if (selectedPR) {
        await purchaseRequisitionService.update(selectedPR.id, dataToSubmit);
        showSnackbar('Purchase requisition updated successfully', 'success');
      } else {
        await purchaseRequisitionService.create(dataToSubmit);
        showSnackbar('Purchase requisition created successfully', 'success');
      }
      setPage(0);
      handleCloseDialog();
      loadPRs();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleAddItem = () => {
    setItems([...items, {
      description: '',
      model_size: '',
      qty: 0,
      unit: 'PC',
      remark: ''
    }]);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this purchase requisition?')) {
      try {
        await purchaseRequisitionService.delete(id);
        showSnackbar('Purchase requisition deleted successfully', 'success');
        loadPRs();
      } catch (error: any) {
        showSnackbar(error.response?.data?.message || 'Failed to delete', 'error');
      }
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting_for_pr': return 'default';
      case 'pr_approved': return 'info';
      case 'waiting_for_quotation': return 'warning';
      case 'quotation_approved': return 'info';
      case 'waiting_for_po': return 'warning';
      case 'po_approved': return 'info';
      case 'waiting_for_goods': return 'primary';
      case 'good_received': return 'success';
      case 'cancel': return 'error';
      case 'hold': return 'warning';
      case 'repair': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'waiting_for_pr': return 'Waiting for PR';
      case 'pr_approved': return 'PR Approved';
      case 'waiting_for_quotation': return 'Waiting for Quotation';
      case 'quotation_approved': return 'Quotation Approved';
      case 'waiting_for_po': return 'Waiting for PO';
      case 'po_approved': return 'PO Approved';
      case 'waiting_for_goods': return 'Waiting for Goods';
      case 'good_received': return 'Good Received';
      case 'cancel': return 'Cancel';
      case 'hold': return 'Hold';
      case 'repair': return 'Repair';
      default: return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const filteredPRs = prs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading && prs.length === 0) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <PRIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Purchase Requisitions
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add PR
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by PR No, Description, Model/Size, Machine, Supplier, Attention..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="waiting_for_pr">Waiting for PR</MenuItem>
              <MenuItem value="pr_approved">PR Approved</MenuItem>
              <MenuItem value="waiting_for_quotation">Waiting for Quotation</MenuItem>
              <MenuItem value="quotation_approved">Quotation Approved</MenuItem>
              <MenuItem value="waiting_for_po">Waiting for PO</MenuItem>
              <MenuItem value="po_approved">PO Approved</MenuItem>
              <MenuItem value="waiting_for_goods">Waiting for Goods</MenuItem>
              <MenuItem value="good_received">Good Received</MenuItem>
              <MenuItem value="cancel">Cancel</MenuItem>
              <MenuItem value="hold">Hold</MenuItem>
              <MenuItem value="repair">Repair</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              label="Attention"
              value={attentionFilter}
              onChange={(e) => setAttentionFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="REGGIANI">REGGIANI</MenuItem>
              <MenuItem value="MS Italy">MS Italy</MenuItem>
              <MenuItem value="HANGZHOU XIONGYUE">HANGZHOU XIONGYUE</MenuItem>
              <MenuItem value="Shaoxing Dongsheng Technology">Shaoxing Dongsheng Technology</MenuItem>
              <MenuItem value="BE Automation">BE Automation</MenuItem>
              <MenuItem value="Spring Tech">Spring Tech</MenuItem>
              <MenuItem value="Fine Finish">Fine Finish</MenuItem>
              <MenuItem value="SPG">SPG</MenuItem>
              <MenuItem value="No Attention">No Attention</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              label="Machine"
              value={machineFilter}
              onChange={(e) => setMachineFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="R1">R1</MenuItem>
              <MenuItem value="R2">R2</MenuItem>
              <MenuItem value="R3">R3</MenuItem>
              <MenuItem value="R4">R4</MenuItem>
              <MenuItem value="R5">R5</MenuItem>
              <MenuItem value="Digital JP7">Digital JP7</MenuItem>
              <MenuItem value="Washer">Washer</MenuItem>
              <MenuItem value="Common Item">Common Item</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              label="PR Type"
              value={prTypeFilter}
              onChange={(e) => setPrTypeFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Local">Local</MenuItem>
              <MenuItem value="Hong-Kong">Hong-Kong</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Supplier"
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Local">Local</MenuItem>
              <MenuItem value="Foreign">Foreign</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setAttentionFilter('');
                setMachineFilter('');
                setPrTypeFilter('');
                setSupplierFilter('');
                setStartDate('');
                setEndDate('');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* PR Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>PR No</TableCell>
              <TableCell>PR Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Model & Size</TableCell>
              <TableCell>Attention</TableCell>
              <TableCell>Machine</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Balance Qty</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPRs.map((pr, idx) => (
              <TableRow key={`${pr.id}-${pr.item_no || '0'}-${idx}`} hover>
                <TableCell>{pr.pr_no}</TableCell>
                <TableCell>{pr.pr_date ? format(new Date(pr.pr_date), 'dd/MM/yyyy') : '-'}</TableCell>
                <TableCell>{pr.description || '-'}</TableCell>
                <TableCell>{pr.model_size || '-'}</TableCell>
                <TableCell>{pr.attention || '-'}</TableCell>
                <TableCell>{pr.machine || '-'}</TableCell>
                <TableCell>{pr.supplier || '-'}</TableCell>
                <TableCell>{pr.request_qty || pr.qty || 0} {pr.unit}</TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={pr.status}
                    onChange={(e) => handleInlineStatusChange(pr, e.target.value)}
                    disabled={updatingStatusId === pr.id}
                    sx={{ minWidth: 170 }}
                  >
                    <MenuItem value="waiting_for_pr">Waiting for PR</MenuItem>
                    <MenuItem value="pr_approved">PR Approved</MenuItem>
                    <MenuItem value="waiting_for_quotation">Waiting for Quotation</MenuItem>
                    <MenuItem value="quotation_approved">Quotation Approved</MenuItem>
                    <MenuItem value="waiting_for_po">Waiting for PO</MenuItem>
                    <MenuItem value="po_approved">PO Approved</MenuItem>
                    <MenuItem value="waiting_for_goods">Waiting for Goods</MenuItem>
                    <MenuItem value="good_received">Good Received</MenuItem>
                    <MenuItem value="cancel">Cancel</MenuItem>
                    <MenuItem value="hold">Hold</MenuItem>
                    <MenuItem value="repair">Repair</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <Chip
                    label={pr.balance_qty || 0}
                    color={pr.balance_qty === 0 ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" color="info" onClick={() => handleViewDetails(pr)}>
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton size="small" color="primary" onClick={() => handleOpenDialog(pr)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(pr.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredPRs.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No purchase requisitions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={prs.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPR ? 'Edit Purchase Requisition' : 'Add Purchase Requisition'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PR Number"
                required
                value={formData.pr_no}
                onChange={(e) => setFormData({ ...formData, pr_no: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PR Date"
                type="date"
                required
                value={formData.pr_date}
                onChange={(e) => setFormData({ ...formData, pr_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Item No"
                value={formData.item_no}
                onChange={(e) => setFormData({ ...formData, item_no: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="PR Type"
                value={formData.pr_type}
                onChange={(e) => setFormData({ ...formData, pr_type: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Local">Local</MenuItem>
                <MenuItem value="Hong-Kong">Hong-Kong</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Attention"
                value={formData.attention}
                onChange={(e) => setFormData({ ...formData, attention: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="REGGIANI">REGGIANI</MenuItem>
                <MenuItem value="SPG">SPG</MenuItem>
                <MenuItem value="MS Italy">MS Italy</MenuItem>
                <MenuItem value="HANGZHOU XIONGYUE">HANGZHOU XIONGYUE</MenuItem>
                <MenuItem value="Shaoxing Dongsheng Technology">Shaoxing Dongsheng Technology</MenuItem>
                <MenuItem value="Fine Finish">Fine Finish</MenuItem>
                <MenuItem value="RBE">RBE</MenuItem>
                <MenuItem value="SEBIM Engineering">SEBIM Engineering</MenuItem>
                <MenuItem value="DSDST">DSDST</MenuItem>
                <MenuItem value="Airline Traders">Airline Traders</MenuItem>
                <MenuItem value="YASIN Engineering">YASIN Engineering</MenuItem>
                <MenuItem value="SPARKLIT">SPARKLIT</MenuItem>
                <MenuItem value="KLUBER">KLUBER</MenuItem>
                <MenuItem value="Euro Heaters">Euro Heaters</MenuItem>
                <MenuItem value="Dilanjana">Dilanjana</MenuItem>
                <MenuItem value="BE Automation">BE Automation</MenuItem>
                <MenuItem value="Honest">Honest</MenuItem>
                <MenuItem value="Chamara Rubber">Chamara Rubber</MenuItem>
                <MenuItem value="Yeman">Yeman</MenuItem>
                <MenuItem value="Spring Tech">Spring Tech</MenuItem>
                <MenuItem value="JK">JK</MenuItem>
                <MenuItem value="SKY TECH">SKY TECH</MenuItem>
                <MenuItem value="ANT">ANT</MenuItem>
                <MenuItem value="DNTT">DNTT</MenuItem>
                <MenuItem value="D Tech Automation">D Tech Automation</MenuItem>
                <MenuItem value="TAD">TAD</MenuItem>
                <MenuItem value="Namal Motors">Namal Motors</MenuItem>
                <MenuItem value="ATLAS">ATLAS</MenuItem>
                <MenuItem value="AQUA Touch">AQUA Touch</MenuItem>
                <MenuItem value="SHAKTHI Traders">SHAKTHI Traders</MenuItem>
                <MenuItem value="TECHMAST Automation">TECHMAST Automation</MenuItem>
                <MenuItem value="Dynamic">Dynamic</MenuItem>
                <MenuItem value="SOAN">SOAN</MenuItem>
                <MenuItem value="EVERBOLT">EVERBOLT</MenuItem>
                <MenuItem value="DUSKY">DUSKY</MenuItem>
                <MenuItem value="CT Energy">CT Energy</MenuItem>
                <MenuItem value="Profitland">Profitland</MenuItem>
                <MenuItem value="UNIFAB">UNIFAB</MenuItem>
                <MenuItem value="JEN HAUR">JEN HAUR</MenuItem>
                <MenuItem value="Mega Heaters">Mega Heaters</MenuItem>
                <MenuItem value="INOX Machinery">INOX Machinery</MenuItem>
                <MenuItem value="ERE Global">ERE Global</MenuItem>
                <MenuItem value="LIBERTY Motor">LIBERTY Motor</MenuItem>
                <MenuItem value="RUBY International">RUBY International</MenuItem>
                <MenuItem value="FIMAT-ITALY">FIMAT-ITALY</MenuItem>
                <MenuItem value="DEBUG">DEBUG</MenuItem>
                <MenuItem value="No Attention">No Attention</MenuItem>
                <MenuItem value="DEMO">DEMO</MenuItem>
                <MenuItem value="Hack Engineer">Hack Engineer</MenuItem>
                <MenuItem value="Atexco">Atexco</MenuItem>
                <MenuItem value="LPG Engineering">LPG Engineering</MenuItem>
                <MenuItem value="Gemtrans Engineering">Gemtrans Engineering</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Machine"
                value={formData.machine}
                onChange={(e) => setFormData({ ...formData, machine: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="R1">R1</MenuItem>
                <MenuItem value="R2">R2</MenuItem>
                <MenuItem value="R3">R3</MenuItem>
                <MenuItem value="R4">R4</MenuItem>
                <MenuItem value="R5">R5</MenuItem>
                <MenuItem value="R1 & R3">R1 & R3</MenuItem>
                <MenuItem value="R1 & R4">R1 & R4</MenuItem>
                <MenuItem value="R1 & R5">R1 & R5</MenuItem>
                <MenuItem value="R3 & R4">R3 & R4</MenuItem>
                <MenuItem value="R3 & R5">R3 & R5</MenuItem>
                <MenuItem value="R4 & R5">R4 & R5</MenuItem>
                <MenuItem value="R1, R3 & R4">R1, R3 & R4</MenuItem>
                <MenuItem value="R1, R4 & R5">R1, R4 & R5</MenuItem>
                <MenuItem value="Digital JP7">Digital JP7</MenuItem>
                <MenuItem value="Atexco">Atexco</MenuItem>
                <MenuItem value="Monti">Monti</MenuItem>
                <MenuItem value="Steamer 01">Steamer 01</MenuItem>
                <MenuItem value="Steamer 02">Steamer 02</MenuItem>
                <MenuItem value="Washer">Washer</MenuItem>
                <MenuItem value="Stripping M/C">Stripping M/C</MenuItem>
                <MenuItem value="Endring M/C">Endring M/C</MenuItem>
                <MenuItem value="Exposing M/C">Exposing M/C</MenuItem>
                <MenuItem value="Sample Steamer">Sample Steamer</MenuItem>
                <MenuItem value="Sample Dryer">Sample Dryer</MenuItem>
                <MenuItem value="Common Item">Common Item</MenuItem>
                <MenuItem value="Coating M/C">Coating M/C</MenuItem>
                <MenuItem value="Strike Off M/C">Strike Off M/C</MenuItem>
                <MenuItem value="Mixing Motor">Mixing Motor</MenuItem>
                <MenuItem value="Hand Screen">Hand Screen</MenuItem>
                <MenuItem value="Baral Washer">Baral Washer</MenuItem>
                <MenuItem value="Fimat">Fimat</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Supplier"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Local">Local</MenuItem>
                <MenuItem value="Foreign">Foreign</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="waiting_for_pr">Waiting for PR</MenuItem>
                <MenuItem value="pr_approved">PR Approved</MenuItem>
                <MenuItem value="waiting_for_quotation">Waiting for Quotation</MenuItem>
                <MenuItem value="quotation_approved">Quotation Approved</MenuItem>
                <MenuItem value="waiting_for_po">Waiting for PO</MenuItem>
                <MenuItem value="po_approved">PO Approved</MenuItem>
                <MenuItem value="waiting_for_goods">Waiting for Goods</MenuItem>
                <MenuItem value="good_received">Good Received</MenuItem>
                <MenuItem value="cancel">Cancel</MenuItem>
                <MenuItem value="hold">Hold</MenuItem>
                <MenuItem value="repair">Repair</MenuItem>
              </TextField>
            </Grid>
            
            {/* Items Section */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  PR Items
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  sx={{ borderColor: '#1976d2', color: '#1976d2' }}
                >
                  Add Item
                </Button>
              </Box>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#f9f9f9' }}>
                {items.map((item, index) => (
                  <Paper key={index} elevation={1} sx={{ p: 2, mb: 2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        Item #{index + 1}
                      </Typography>
                      {items.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description"
                          required
                          multiline
                          rows={2}
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Model & Size"
                          value={item.model_size}
                          onChange={(e) => handleItemChange(index, 'model_size', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Quantity"
                          type="number"
                          required
                          value={item.qty}
                          onChange={(e) => handleItemChange(index, 'qty', parseFloat(e.target.value) || 0)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          select
                          label="Unit"
                          required
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        >
                          <MenuItem value="PC">PC</MenuItem>
                          <MenuItem value="Kg">Kg</MenuItem>
                          <MenuItem value="L">L</MenuItem>
                          <MenuItem value="M">M</MenuItem>
                          <MenuItem value="JOB">JOB</MenuItem>
                          <MenuItem value="Length">Length</MenuItem>
                          <MenuItem value="Ltr">Ltr</MenuItem>
                          <MenuItem value="Set">Set</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Remark"
                          multiline
                          rows={2}
                          value={item.remark}
                          onChange={(e) => handleItemChange(index, 'remark', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SAP Code"
                value={formData.sap_code}
                onChange={(e) => setFormData({ ...formData, sap_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SAP PR"
                value={formData.sap_pr}
                onChange={(e) => setFormData({ ...formData, sap_pr: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SAP PO"
                value={formData.sap_po}
                onChange={(e) => setFormData({ ...formData, sap_po: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="GRN"
                value={formData.grn}
                onChange={(e) => setFormData({ ...formData, grn: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Delivery Method"
                value={formData.delivery_method}
                onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedPR ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle>Purchase Requisition Details</DialogTitle>
        <DialogContent>
          {viewPR && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">PR Number</Typography>
                <Typography variant="body1" fontWeight="bold">{viewPR.pr_no}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">PR Date</Typography>
                <Typography variant="body1">{viewPR.pr_date ? format(new Date(viewPR.pr_date), 'dd/MM/yyyy') : '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Item No</Typography>
                <Typography variant="body1">{viewPR.item_no || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">PR Type</Typography>
                <Typography variant="body1">{viewPR.pr_type || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Attention</Typography>
                <Typography variant="body1">{viewPR.attention || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={getStatusLabel(viewPR.status)}
                  color={getStatusColor(viewPR.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Description</Typography>
                <Typography variant="body1">{viewPR.description || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Machine</Typography>
                <Typography variant="body1">{viewPR.machine || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Model & Size</Typography>
                <Typography variant="body1">{viewPR.model_size || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Supplier</Typography>
                <Typography variant="body1">{viewPR.supplier || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Delivery Method</Typography>
                <Typography variant="body1">{viewPR.delivery_method || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Unit</Typography>
                <Typography variant="body1">{viewPR.unit || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Request Qty</Typography>
                <Typography variant="body1">{viewPR.request_qty || viewPR.qty || 0}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Issued Qty</Typography>
                <Typography variant="body1">{viewPR.issued_qty || 0}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Balance Qty</Typography>
                <Chip
                  label={viewPR.balance_qty || 0}
                  color={viewPR.balance_qty === 0 ? 'success' : 'warning'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">SAP Code</Typography>
                <Typography variant="body1">{viewPR.sap_code || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">SAP PR</Typography>
                <Typography variant="body1">{viewPR.sap_pr || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">SAP PO</Typography>
                <Typography variant="body1">{viewPR.sap_po || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">GRN</Typography>
                <Typography variant="body1">{viewPR.grn || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Quotation Ok Date</Typography>
                <Typography variant="body1">{viewPR.quotation_ok_date ? format(new Date(viewPR.quotation_ok_date), 'dd/MM/yyyy') : '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Received Date</Typography>
                <Typography variant="body1">{viewPR.received_date ? format(new Date(viewPR.received_date), 'dd/MM/yyyy') : '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Location</Typography>
                <Typography variant="body1">{viewPR.location || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Remark</Typography>
                <Typography variant="body1">{viewPR.remark || '-'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Showing selected PR item row details only.
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PRManagement;
